import { useState, type FormEvent } from "react";
import { ApiError, api } from "../lib/api.ts";
import { useApi, formatDate, titleCase } from "../lib/hooks.ts";
import { useAuth } from "../lib/auth.tsx";
import type { Role, User } from "../lib/types.ts";
import { Badge, Card, Empty, ErrorAlert, Modal, Spinner } from "../components/ui.tsx";

export function UsersPage() {
  const { user: me, can } = useAuth();
  const users = useApi<{ data: User[] }>("/api/v3/users");
  const roles = useApi<{ data: Role[] }>("/api/v3/roles");

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "viewer", password: "" });

  const managed = can("users.manage");

  async function run(action: () => Promise<unknown>, after?: () => void) {
    setBusy(true);
    setError(null);
    try {
      await action();
      after?.();
      users.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha na operacao");
    } finally {
      setBusy(false);
    }
  }

  async function createUser(event: FormEvent) {
    event.preventDefault();
    await run(
      () => api("/api/v3/users", { method: "POST", body: form }),
      () => {
        setOpen(false);
        setForm({ name: "", email: "", role: "viewer", password: "" });
      },
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Usuarios e papeis</h1>
          <p className="subtle">Controle de acesso por papel com permissoes por recurso.</p>
        </div>
        <span className="spacer" />
        <button className="btn primary" disabled={!managed} onClick={() => { setError(null); setOpen(true); }}>
          Convidar usuario
        </button>
      </div>

      {error ? <ErrorAlert message={error} /> : null}

      <Card title="Papeis disponiveis">
        <div className="grid cols-4">
          {(roles.data?.data ?? []).map((r) => (
            <div key={r.role} className="card" style={{ padding: 14 }}>
              <strong>{titleCase(r.role)}</strong>
              <div className="cell-sub" style={{ marginTop: 6 }}>
                {r.permissions.map((p) => (
                  <div key={p} className="mono">
                    {p}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card tight>
        {users.loading && !users.data ? (
          <div className="empty"><Spinner /></div>
        ) : !users.data?.data.length ? (
          <Empty>Nenhum usuario cadastrado.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Papel</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data.data.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="cell-title">{u.name}</div>
                      <div className="cell-sub">{u.email}</div>
                    </td>
                    <td>
                      <select
                        className="select"
                        style={{ width: 150 }}
                        value={u.role}
                        disabled={!managed || String(me?.id) === String(u.id)}
                        onChange={(e) =>
                          run(() => api(`/api/v3/users/${u.id}`, { method: "PATCH", body: { role: e.target.value } }))
                        }
                      >
                        {(roles.data?.data ?? []).map((r) => (
                          <option key={r.role} value={r.role}>
                            {titleCase(r.role)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <Badge value={u.status} />
                    </td>
                    <td className="cell-sub">{u.createdAt ? formatDate(u.createdAt) : "—"}</td>
                    <td>
                      <div className="toolbar">
                        <button
                          className="btn sm"
                          disabled={!managed || String(me?.id) === String(u.id)}
                          onClick={() =>
                            run(() =>
                              api(`/api/v3/users/${u.id}`, {
                                method: "PATCH",
                                body: { status: u.status === "active" ? "suspended" : "active" },
                              }),
                            )
                          }
                        >
                          {u.status === "active" ? "Suspender" : "Restaurar"}
                        </button>
                        <button
                          className="btn sm danger"
                          disabled={!managed || String(me?.id) === String(u.id)}
                          onClick={() => {
                            if (window.confirm(`Excluir ${u.email}?`)) {
                              run(() => api(`/api/v3/users/${u.id}`, { method: "DELETE" }));
                            }
                          }}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open ? (
        <Modal
          title="Convidar usuario"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button className="btn" onClick={() => setOpen(false)} disabled={busy}>
                Cancelar
              </button>
              <button className="btn primary" form="user-form" disabled={busy}>
                {busy ? "Enviando..." : "Criar usuario"}
              </button>
            </>
          }
        >
          <form id="user-form" onSubmit={createUser} style={{ display: "grid", gap: 12 }}>
            <div className="field">
              <label htmlFor="u-name">Nome</label>
              <input id="u-name" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="field">
              <label htmlFor="u-email">E-mail</label>
              <input id="u-email" className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="field">
              <label htmlFor="u-role">Papel</label>
              <select id="u-role" className="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {(roles.data?.data ?? []).map((r) => (
                  <option key={r.role} value={r.role}>
                    {titleCase(r.role)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="u-pass">Senha provisoria</label>
              <input
                id="u-pass"
                className="input"
                type="password"
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
              <span className="muted" style={{ fontSize: 12 }}>
                Minimo de 8 caracteres.
              </span>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
